import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Phone, MapPin } from "lucide-react";

const EFFECTIVE_DATE = "May 28, 2026";

const sections = [
  {
    title: "Introduction",
    body: (
      <>
        At <span className="text-white">Impact Legacy Group</span>, your privacy is our priority. We are committed to protecting the personal information you provide to us when requesting life insurance quotes or purchasing insurance policies. This Privacy Policy explains how we collect, use, and protect your information. We want to assure you that <span className="text-white">we do not sell, share, or distribute your personal data to any third parties under any circumstances</span>.
      </>
    ),
  },
  {
    title: "Information We Collect",
    body: (
      <>
        When you interact with Impact Legacy Group, we may collect the following types of personal information:
        <ul className="list-none mt-4 space-y-2 pl-0">
          <li><span className="text-[#D4AF37]">·</span> <span className="text-white">Contact Information:</span> Name, address, phone number, email address.</li>
          <li><span className="text-[#D4AF37]">·</span> <span className="text-white">Demographic Information:</span> Age, gender, marital status, and occupation.</li>
          <li><span className="text-[#D4AF37]">·</span> <span className="text-white">Insurance Information:</span> Details about your coverage needs, beneficiaries, and any relevant health or lifestyle information required for insurance applications.</li>
        </ul>
      </>
    ),
  },
  {
    title: "How We Use Your Information",
    body: (
      <>
        The information we collect is used solely for the purpose of providing our services to you. Specifically, we use your information to:
        <ul className="list-none mt-4 space-y-2 pl-0">
          <li><span className="text-[#D4AF37]">·</span> Generate life insurance quotes tailored to your needs.</li>
          <li><span className="text-[#D4AF37]">·</span> Process and submit your insurance applications to insurance carriers.</li>
          <li><span className="text-[#D4AF37]">·</span> Communicate with you regarding your policies, quotes, or any inquiries.</li>
        </ul>
      </>
    ),
  },
  {
    title: "When And With Whom Do We Share Your Personal Information?",
    body: (
      <>
        <p className="italic text-white/55 mb-4">In short: We may share information in specific situations described in this section and/or with the following third parties. We do not sell personal information.</p>
        We may need to share your personal information in the following areas:
        <ul className="list-none mt-4 space-y-3 pl-0">
          <li><span className="text-[#D4AF37]">·</span> <span className="text-white">Business Transfers.</span> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.</li>
          <li><span className="text-[#D4AF37]">·</span> <span className="text-white">Service Providers.</span> We may share your information with third-party vendors and service providers that help us with specialized services, including billing, payment processing, customer service, email deployment, business analytics, marketing, advertising, performance monitoring, hosting, and data processing.</li>
          <li><span className="text-[#D4AF37]">·</span> <span className="text-white">Law Enforcement.</span> We may disclose information to respond to subpoenas, court orders, legal process, law enforcement requests, legal claims or government inquiries, and to protect and defend the rights, interests, health, safety, and security of Impact Legacy Group, our affiliates, users, or the public.</li>
          <li><span className="text-[#D4AF37]">·</span> <span className="text-white">With Your Consent.</span> We may share information for any other purposes disclosed to you at the time we collect the information or pursuant to your consent or direction.</li>
        </ul>
      </>
    ),
  },
  {
    title: "No Sale Or Sharing Of Data",
    body: (
      <>
        Impact Legacy Group does not sell, trade, or share your personal information with third parties for marketing purposes or any other purposes. Your information is strictly used within our organization to assist you with your insurance needs.
        <p className="mt-4">The only exception to this is when we need to share information with insurance carriers to process your insurance applications. In these cases, your data is shared securely and only with your explicit consent.</p>
        <p className="mt-4 text-white">Phone numbers collected for SMS consent and SMS opt-in data will not be shared with third parties or affiliates for marketing or promotional purposes.</p>
      </>
    ),
  },
  {
    title: "Data Protection And Security",
    body: (
      <>
        We implement a variety of security measures to maintain the safety of your personal information, including:
        <ul className="list-none mt-4 space-y-2 pl-0">
          <li><span className="text-[#D4AF37]">·</span> Encrypted storage of data.</li>
          <li><span className="text-[#D4AF37]">·</span> Access controls to limit who can view or process your information.</li>
          <li><span className="text-[#D4AF37]">·</span> Regular monitoring of our systems to prevent unauthorized access.</li>
        </ul>
      </>
    ),
  },
  {
    title: "Your Rights",
    body: (
      <>
        You have the following rights concerning your personal information:
        <ul className="list-none mt-4 space-y-2 pl-0">
          <li><span className="text-[#D4AF37]">·</span> <span className="text-white">Access:</span> You may request a copy of the information we have about you.</li>
          <li><span className="text-[#D4AF37]">·</span> <span className="text-white">Correction:</span> You can request corrections to any inaccurate or incomplete information.</li>
          <li><span className="text-[#D4AF37]">·</span> <span className="text-white">Deletion:</span> You can request the deletion of your personal information, subject to applicable laws.</li>
          <li><span className="text-[#D4AF37]">·</span> <span className="text-white">Withdrawal of Consent:</span> You can withdraw your consent for us to process your data at any time.</li>
        </ul>
        <p className="mt-4">To exercise any of these rights, please contact us using the information provided below.</p>
      </>
    ),
  },
  {
    title: "Changes To This Privacy Policy",
    body: (
      <>
        Impact Legacy Group reserves the right to update this Privacy Policy as needed to reflect changes in our practices or relevant laws. We will notify you of any significant changes and provide the updated Privacy Policy on our website.
      </>
    ),
  },
];

export default function Privacy() {
  return (
    <div data-testid="privacy-page" className="pt-32 pb-32">
      <section className="max-w-3xl mx-auto px-6 md:px-10">
        <div className="overline mb-6">— Privacy Policy</div>
        <h1 className="font-serif text-5xl md:text-6xl text-white tracking-tighter leading-[1] mb-6">
          Your privacy. <span className="italic text-[#D4AF37]">Our promise.</span>
        </h1>
        <div className="text-xs text-white/45 tracking-[0.22em] uppercase mb-10">Effective Date: {EFFECTIVE_DATE}</div>

        <div className="gold-line mb-12" />

        <div className="space-y-12">
          {sections.map((s, i) => (
            <div key={s.title} data-testid={`privacy-section-${i}`}>
              <div className="flex items-baseline gap-4 mb-4">
                <span className="font-serif text-2xl text-[#D4AF37]">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="font-serif text-2xl md:text-3xl text-white leading-tight">{s.title}</h2>
              </div>
              <div className="text-white/70 leading-relaxed pl-10 space-y-2">{s.body}</div>
            </div>
          ))}
        </div>

        <div className="mt-16 p-8 border border-[#D4AF37]/30 bg-[#1A1A1A]/60">
          <div className="flex items-baseline gap-4 mb-5">
            <span className="font-serif text-2xl text-[#D4AF37]">09</span>
            <h2 className="font-serif text-2xl md:text-3xl text-white leading-tight">Contact Us</h2>
          </div>
          <p className="text-white/70 leading-relaxed mb-6 pl-10">
            If you have any questions about this Privacy Policy or your personal information, please contact us:
          </p>
          <div className="pl-10 space-y-3">
            <div className="text-white font-medium">Impact Legacy Group</div>
            <a href="mailto:impactlegacy.davis@gmail.com" data-testid="privacy-contact-email" className="flex items-center gap-3 text-white/75 hover:text-[#D4AF37] transition-colors">
              <Mail className="w-4 h-4 text-[#D4AF37]" /> impactlegacy.davis@gmail.com
            </a>
            <a href="tel:9364435365" data-testid="privacy-contact-phone" className="flex items-center gap-3 text-white/75 hover:text-[#D4AF37] transition-colors">
              <Phone className="w-4 h-4 text-[#D4AF37]" /> 936-443-5365
            </a>
            <div className="flex items-start gap-3 text-white/75">
              <MapPin className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" />
              <span>12218 Medina Mill, San Antonio, TX 78253</span>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-wrap gap-6 text-sm">
          <Link data-testid="privacy-to-terms" to="/terms" className="inline-flex items-center gap-2 text-[#D4AF37] uppercase tracking-[0.22em] text-xs border-b border-[#D4AF37]/40 pb-1 hover:gap-4 transition-all">
            Read Terms & Conditions <ArrowRight className="w-4 h-4" />
          </Link>
          <Link data-testid="privacy-to-contact" to="/contact" className="inline-flex items-center gap-2 text-white/70 uppercase tracking-[0.22em] text-xs border-b border-white/20 pb-1 hover:text-white hover:gap-4 transition-all">
            Contact Us <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
